import React from 'react';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';

// Input Components (they now handle their own output display)
import EquifaxInputComponent from './input/EquifaxInputComponent';
import BankStatementInputComponent from './input/BankStatementInputComponent';
import PANDetailsInputComponent from './input/PANDetailsInputComponent';
import PAN2InputComponent from './input/PAN2InputComponent';
import EmploymentHistoryInputComponent from './input/EmploymentHistoryInputComponent';
import EHFUANInputComponent from './input/EHFUANInputComponent';
import DrivingLicenceInputComponent from './input/DrivingLicenceInputComponent';
import VoterIdInputComponent from './input/VoterIdInputComponent';
import CAMembershipInputComponent from './input/CAMembershipInputComponent';
import MobileBureauLookupInputComponent from './input/MobileBureauLookupInputComponent';


const DynamicReportComponent = ({ reportType, serviceId, serviceDetails, leadId, initialValues, initialReportData }) => {
    // Normalize report type to handle different formats
    const normalizeReportType = (type) => {
        if (!type) return type;
        
        // Convert hyphenated lowercase to proper case
        const typeMap = {
            'equifax': 'Equifax',
            'equifax-gridline': 'Equifax',
            'bank-statement-analysis': 'Bank Statement Analysis',
            'bank-statement': 'Bank Statement Analysis',
            'pan-details': 'PAN Details',
            // V2 PAN service uses a dedicated component (PAN2InputComponent)
            // because it hits a different backend endpoint (/gridlines/pan-details)
            // than v1 (/tartan/get-details-from-pan). Routing v2 to v1 would
            // cause a 422 because the request body shape differs.
            'pan-details-v2': 'PAN Details V2',
            'pan': 'PAN Details',
            'employment-history-from-mobile-comprehensive': 'Employment History by Mobile',
            'employment-history': 'Employment History by Mobile',
            'uan-employment-history': 'Employment History by UAN',
            'uan-number-to-employment-history-with-doe': 'Employment History by UAN',
            'uannumbertoemploymenthistorywithdoe': 'Employment History by UAN',
            'uan-verification': 'UAN',
            'uan': 'UAN',
            'driving-licence-detailed': 'Driving Licence',
            'driving-licence': 'Driving Licence',
            // V2 Driving Licence — same input shape as v1.
            'driving-licence-v2': 'Driving Licence',
            'dl-verification': 'Driving Licence',
            'voter-id-validation': 'Voter ID',
            'voter-id': 'Voter ID',
            // V2 Voter ID — same input shape.
            'voter-id-details-v2': 'Voter ID',
            'epic-verification': 'Voter ID',
            'aadhaarverificationwithotp': 'Aadhaar OTP',
            'aadhaar-verification-with-otp': 'Aadhaar OTP',
            'aadhaar-otp': 'Aadhaar OTP',
            'ca-membership-verification': 'CA Membership Verification',
            'ca-membership': 'CA Membership Verification',
            'mobile-bureau-lookup': 'Mobile Bureau Lookup',
            'mobile-bureau': 'Mobile Bureau Lookup',
            'mobile-lookup': 'Mobile Bureau Lookup',
        };
        
        // Check if it's a lowercase hyphenated format
        const lowerType = type.toLowerCase();
        if (typeMap[lowerType]) {
            return typeMap[lowerType];
        }
        
        // Return original if not found in map
        return type;
    };

    const normalizedReportType = normalizeReportType(reportType);

    // Resolve which input component handles this report type. `leadId` is
    // optional — when present (e.g. when this is rendered inside the Lead
    // Wizard's verification Sidebar), the input component forwards it as
    // a request-body field so the saved BureauBoxReport row gets linked
    // to the lead via the model's `creating` hook.
    const componentByType = {
        'Equifax': EquifaxInputComponent,
        'Bank Statement Analysis': BankStatementInputComponent,
        'PAN Details': PANDetailsInputComponent,
        'PAN Details V2': PAN2InputComponent,
        'Employment History by Mobile': EmploymentHistoryInputComponent,
        'Employment History by UAN': EHFUANInputComponent,
        'UAN': PANDetailsInputComponent,
        'Driving Licence': DrivingLicenceInputComponent,
        'Voter ID': VoterIdInputComponent,
        'CA Membership Verification': CAMembershipInputComponent,
        'Mobile Bureau Lookup': MobileBureauLookupInputComponent,
    };

    const Component = componentByType[normalizedReportType];
    if (!Component) {
        return (
            <Card>
                <Message
                    severity="warn"
                    text={`Report type "${reportType}" is not supported yet.`}
                    className="w-full"
                />
            </Card>
        );
    }

    return (
        <Component
            serviceId={serviceId}
            serviceDetails={serviceDetails}
            leadId={leadId}
            initialValues={initialValues}
            initialReportData={initialReportData}
        />
    );
};

export default DynamicReportComponent;